import { Component } from '@theme/component';

/**
 * Light/dark theme toggle.
 *
 * The store follows the operating system by default — `snippets/color-schemes.liquid` emits
 * its dark overrides under `prefers-color-scheme: dark`. This control writes an explicit
 * `data-theme` attribute on `<html>`, which takes precedence over that media query, and
 * remembers the choice in localStorage. A small inline script in `layout/theme.liquid`
 * replays the stored value before first paint so there is no flash of the wrong theme.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} themeToggleButton – The toggle control.
 *
 * @extends Component<Refs>
 */
class ThemeToggle extends Component {
  requiredRefs = ['themeToggleButton'];

  /** @type {MediaQueryList} */
  #media = window.matchMedia('(prefers-color-scheme: dark)');

  static #STORAGE_KEY = 'keyora:theme';

  /**
   * The theme currently in effect — an explicit choice if one has been made, otherwise
   * whatever the operating system asks for.
   *
   * @returns {'dark' | 'light'}
   */
  get #currentTheme() {
    const explicit = document.documentElement.getAttribute('data-theme');
    if (explicit === 'dark' || explicit === 'light') return explicit;
    return this.#media.matches ? 'dark' : 'light';
  }

  /**
   * Keeps the control's accessible state in step with the theme, including when the OS
   * flips while the page is open and no explicit choice has been made.
   */
  #syncState = () => {
    const isDark = this.#currentTheme === 'dark';
    const button = this.refs.themeToggleButton;

    button.setAttribute('aria-pressed', String(isDark));
    button.setAttribute('aria-label', isDark ? button.dataset.labelLight : button.dataset.labelDark);
  };

  connectedCallback() {
    super.connectedCallback();
    this.#syncState();
    this.#media.addEventListener('change', this.#syncState);
  }

  disconnectedCallback() {
    this.#media.removeEventListener('change', this.#syncState);
    super.disconnectedCallback();
  }

  /**
   * Flips the theme and persists the choice.
   *
   * @param {Event} event - The click event
   */
  toggle = (event) => {
    event.preventDefault();

    const next = this.#currentTheme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);

    try {
      localStorage.setItem(ThemeToggle.#STORAGE_KEY, next);
    } catch (error) {
      // Storage can be unavailable (private browsing, blocked cookies). The theme still
      // switches for this page view; it just won't be remembered.
    }

    this.#syncState();
  };
}

if (!customElements.get('theme-toggle')) {
  customElements.define('theme-toggle', ThemeToggle);
}
